import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Lock, ShieldAlert } from 'lucide-react';

/**
 * 4-digit PIN prompt for tournament scoring access.
 * Renders a controlled dialog with one OTP-style input row. On submit it stores
 * the PIN in sessionStorage under `loveall:scoring_pin:<tournament_id>` so the
 * api.js interceptor automatically attaches it to subsequent score/start calls.
 *
 * Props:
 *  - open:           boolean
 *  - onOpenChange:   (open) => void
 *  - tournamentId:   string
 *  - onSubmit:       (pin: string) => Promise<boolean>   // return true on success
 *  - errorMessage?:  string  parent-provided error (e.g. last attempt failed)
 */
export default function ScoringPinDialog({ open, onOpenChange, tournamentId, onSubmit, errorMessage }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Reset on open
  useEffect(() => {
    if (open) {
      setDigits(['', '', '', '']);
      setLocalError('');
      setTimeout(() => inputRefs[0].current?.focus(), 60);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (idx, value) => {
    const v = (value || '').replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setLocalError('');
    if (v && idx < 3) inputRefs[idx + 1].current?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && idx < 3) {
      inputRefs[idx + 1].current?.focus();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      e.preventDefault();
      const next = pasted.split('');
      setDigits(next);
      inputRefs[3].current?.focus();
    }
  };

  const submit = async () => {
    const pin = digits.join('');
    if (pin.length !== 4) {
      setLocalError('Enter the full 4-digit PIN');
      return;
    }
    setSubmitting(true);
    try {
      // Stash immediately so the api.js interceptor uses it on the retry call.
      sessionStorage.setItem(`loveall:scoring_pin:${tournamentId}`, pin);
      const ok = await onSubmit(pin);
      if (!ok) {
        // Wrong PIN — clear from session so future calls don't carry the bad value.
        sessionStorage.removeItem(`loveall:scoring_pin:${tournamentId}`);
        setLocalError('Incorrect PIN. Ask the tournament creator for the latest 4-digit code.');
        setDigits(['', '', '', '']);
        setTimeout(() => inputRefs[0].current?.focus(), 50);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = errorMessage || localError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="scoring-pin-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center">
              <Lock className="h-5 w-5 text-accent" />
            </div>
            <div>
              <DialogTitle className="text-base">Enter Scoring PIN</DialogTitle>
              <DialogDescription className="text-xs">
                Ask the tournament creator — the PIN stays the same for the whole tournament.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex justify-center gap-3 py-4" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={inputRefs[i]}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-md bg-background focus:border-accent focus:outline-none transition-colors"
              data-testid={`pin-digit-${i}`}
              aria-label={`PIN digit ${i + 1}`}
            />
          ))}
        </div>

        {displayError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive" data-testid="pin-error">
            <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{displayError}</span>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="pin-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || digits.join('').length !== 4} data-testid="pin-submit-btn">
            {submitting ? 'Verifying…' : 'Unlock Scoring'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
